// @ts-nocheck -- untyped react-contextmenu JSX props + loose arg-count calls; canvas ECG viewer (see migration report)

/* eslint-disable-next-line local/require-storybook-story --
 * FabricJS canvas host (docs/project-structure.md: widgets/ecg-viewer is "E2E/manual verify, no
 * unit tests"). componentDidMount instantiates ECGChart — which builds a fabric.Canvas — onto the
 * raw <canvas> elements this file renders, then binds listeners to the queried canvas node, all
 * driven by a decoded ECG sample buffer no fixture can stand in for. The chart paints into the
 * canvas bitmap, so there are no DOM locators for axe to survey either way. Same class as the
 * already-exempt pages/studies-list/ui/studiesTable/viewLiveEcgModal/fabricJs/ecgViewer.tsx.
 * Its presentational toolbar parts each have their own story (ECGToolButton, BeatMarkerButtons,
 * ToggleShChannel, ToggleInput). Covered by E2E/manual verification, not Storybook. */
import { CustomTooltip, IconButton, NormalButton, CustomButton } from '@/shared/ui/components';
import SnackbarMaterial from '@mui/material/Snackbar';
import { Dropdown, Popover, Radio, Row } from 'antd';
import _ from 'lodash';
import { Component } from 'react';
import { TwitterPicker } from 'react-color';
import * as ReactContextMenu from 'react-contextmenu';
import { ReactSVG } from 'react-svg';
import { t } from 'ttag';
import ToggleInput from './toggleInput/toggleInput';
import ToggleShChannel from '../view/toolbar/toggleShChannel/toggleShChannel';
import { ANNOTATION_TYPE, EVENT_STATUSES, TOOLBAR_TYPE } from '../../config';
import '../helper/styles.css';
import AddRemoveBeatMarkerIconZoomOutIcon from '../view/toolbar/assets/add-remove-beat-marker-icon.svg';
import captureIconGreen from '../view/toolbar/assets/capture-icon-green.svg';
import ExitViewAllIcon from '../view/toolbar/assets/exit-view-all-icon.svg';
import FineGridIcon from '../view/toolbar/assets/fine-grid-icon.svg';
import navigationLeftIcon from '../view/toolbar/assets/navigation-left.svg';
import navigationRightIcon from '../view/toolbar/assets/navigation-right.svg';
import NormalGridIcon from '../view/toolbar/assets/normal-grid-icon.svg';
import ViewAllIcon from '../view/toolbar/assets/view-all-icon.svg';
import ZoomInIcon from '../view/toolbar/assets/zoom-in-icon.svg';
import ZoomOutIcon from '../view/toolbar/assets/zoom-out-icon.svg';
import BeatMarkerButtons from '../view/toolbar/beatMarkerButtons';
import ECGToolButton from '../view/toolbar/ecgToolButton';
import '../styles.scss';
import { ECGChart } from './ecgChart';
import type { ECGViewerProps } from './ecgViewer.types';

const initialStateValue = {
  isShowBeatMarkers: true,
  shouldDelete: false,
  isClickFineGrid: false,
  isInARBeatMode: false,
  isInWaitingZoomMode: false,
  isInZoomMode: false,
  isInViewAllMode: false,
  spdValue: 25,
  ampValue: 10,
  hiPValue: 0,
  loPValue: 0,
  prevChanIdx: 1,
  dataLength: 3,
  visibleChannels: [0, 1, 2],
  beforeZoomVisibleChannels: [0, 1, 2],
  isInMode: false,
  beatTypeNumber: 1,
};

// react-contextmenu's own prop types are stricter than this legacy JSX usage, so widen them here.
const ContextMenu = ReactContextMenu.ContextMenu as any;
const ContextMenuTrigger = ReactContextMenu.ContextMenuTrigger as any;
const MenuItem = ReactContextMenu.MenuItem as any;
const SubMenu = ReactContextMenu.SubMenu as any;

/**
 * Interactive ECG waveform viewer for study review, owning the canvas chart plus its speed, gain, filter, zoom, view-all and beat-marker toolbar state. The constructor seeds state from initialStateValue with a 10 second capture default and highlight channel 1, builds the 10s/20s/30s capture and CH1-CH3 highlight option lists, binds the object-select and draw hover handlers, and publishes exportImage and handleSubmit to the parent through the exportImage and setClick props.
 */
class ECGViewer extends Component<ECGViewerProps, any> {
  [key: string]: any;

  constructor(props: ECGViewerProps) {
    super(props);
    this.state = {
      ...initialStateValue,
      ecgChart: {},
      isLoadedData: false,
      isVisibleCapturePopover: false,
      captureDuration: 10,
      highlightChannel: 1,
      disabledCaptureDurationIndexes: [],
      disabledHighlightChannelIndexes: [],
      isCapturingImages: false,
    };
    this.arrayChannelPrevChan = ['CH1', 'CH2', 'CH3'];
    this.haveCompleted = false;
    this.isClickFineGridCurrent = false;
    this.isInViewAllModeCurrent = false;
    this.isSelectingHLine = false;
    this.isSelectingVLine = false;
    this.isSelectingPattern = false;
    this.captureDurationOptions = [
      {
        label: t`10s`,
        value: 10,
      },
      {
        label: t`20s`,
        value: 20,
      },
      {
        label: t`30s`,
        value: 30,
      },
    ];
    this.highlightChannelOptions = [
      {
        label: t`CH1`,
        value: 0,
      },
      {
        label: t`CH2`,
        value: 1,
      },
      {
        label: t`CH3`,
        value: 2,
      },
    ];
    this.eventSelectObject = this.selectObject.bind(this);
    this.eventDrawMouseOver = this.handleDrawMouseOver.bind(this);
    this.eventDrawMouseOut = this.handleDrawMouseOut.bind(this);
    this.timeoutSnackbar = null;

    if (this.props.exportImage) {
      this.props.exportImage(this.exportImage);
    }
    if (this.props.setClick) {
      this.props.setClick(this.handleSubmit);
    }
  }

  /**
   * Builds the ECG chart on mount when ECG sample data is present, deriving speed, gain, window duration and filter settings with 25 mm/s, 10 mm/mV and 10 s defaults. Instantiates ECGChart with the raw samples, beat markers, annotations (null in full-event-chart mode) and a callback bundle, caches the canvas object element, attaches the event listeners, then stores chart state and triggers image export or AR-beat mode from the setState callback.
   *
   * @returns Nothing.
   */
  componentDidMount() {
    const { ecg } = this.props;
    if (!_.isEmpty(ecg)) {
      const {
        configChart,
        missingSampleLength,
        isShowViewFullEventChart,
        isInitalARBeatMode,
        timeObject,
        startStudyDate,
        facilityCountry,
        facilityTimezone,
        idComponents,
      } = this.props;
      const { isDisplayScrollbar } = configChart;
      const {
        dataSamples,
        beatMarkers,
        viewerObject,
        hpFilters,
        notchFilters,
        hiddenChannels,
        eventPos,
        diagnosticLead,
        isShowBeatMarkers,
        speed,
        amp,
        wndDuration,
        samplingRate,
        hesBeatStatus,
        isShowVES,
        isShowSVES,
        missingSampleLengthPre,
        missingSampleLengthPost,
      } = ecg;

      const config = {
        speed: speed ?? 25,
        gain: amp ?? 10,
        wndDuration: wndDuration ?? 10,
        LFF: hpFilters ?? 0,
        HFF: notchFilters ?? 0,
        prevChanIdx:
          hiddenChannels && hiddenChannels.length && _.includes(hiddenChannels, diagnosticLead - 1)
            ? diagnosticLead - 1
            : 0,
        isShowBeatMarkers,
        samplingRate,
      };
      const annotations =
        !isShowViewFullEventChart && viewerObject ? viewerObject.widgets : JSON.parse('null');

      const objectFunctions = {
        updateSelectedBeatMarker: this.updateSelectedBeatMarker,
        updateMinMaxHr: this.props.updateMinMaxHr,
        updatePrevChanIdx: this.updatePrevChanIdx,
        updateVisibleChannels: this.updateVisibleChannels,
        updateSpdMenu: this.updateSpd,
        updateAmpMenu: this.updateAmp,
        handleChangeHiLoPDropdown: this.handleChangeHiLoPDropdown,
        toggleViewAllMode: this.toggleViewAllMode,
        updateCompletedStatus: this.updateCompletedStatus,
        updateWaitingZoomModeStatus: this.updateWaitingZoomModeStatus,
        updateZoomModeStatus: this.updateZoomModeStatus,
        updateARBeatModeStatus: this.updateARBeatModeStatus,
        updateSelectingHLineStatus: this.updateSelectingHLineStatus,
        updateSelectingVLineStatus: this.updateSelectingVLineStatus,
        updateSelectingPatternStatus: this.updateSelectingPatternStatus,
        handleOpenSnackbar: this.handleOpenSnackbar,
        updateBeatTypeNumber: this.updateBeatTypeNumber,
        updateSelectingBeatStatus: this.updateSelectingBeatStatus,
        handleUpdateBeat: this.props.handleUpdateBeat,
      };

      const ecgChart = new ECGChart({
        rawDataFromNetwork: dataSamples,
        config,
        beatMarkerArr: beatMarkers,
        hesBeatStatus,
        annotations,
        functionObject: objectFunctions,
        isDisplayScrollbar,
        hiddenChannels,
        eventPos,
        missingSampleLength,
        isShowVES,
        isShowSVES,
        diagnosticLead,
        timeObject,
        startStudyDate,
        facilityCountry,
        facilityTimezone,
        idComponents,
        missingSampleLengthPre,
        missingSampleLengthPost,
      });

      this.canvasObject = document.getElementsByClassName(
        `canvas-object-${idComponents.canvasObject}`,
      )[0];
      this.handleAddEventListener();

      const setStateObject = {
        ecgChart,
        prevChanIdx:
          hiddenChannels && hiddenChannels.length && _.includes(hiddenChannels, diagnosticLead - 1)
            ? diagnosticLead - 1
            : 0,
        dataLength: dataSamples ? dataSamples.length : 0,
        isLoadedData: true,
        spdValue: speed ?? 25,
        ampValue: amp ?? 10,
      };

      if (hiddenChannels && hiddenChannels.length) {
        _.assign(setStateObject, {
          beforeZoomVisibleChannels: hiddenChannels,
          visibleChannels: hiddenChannels,
        });
      }

      if (!_.isNil(hpFilters)) {
        _.assign(setStateObject, { hiPValue: hpFilters });
      }
      if (!_.isNil(notchFilters)) {
        _.assign(setStateObject, { loPValue: notchFilters });
      }
      if (!_.isNil(isShowBeatMarkers)) {
        _.assign(setStateObject, { isShowBeatMarkers });
      }
      this.setState(setStateObject, () => {
        if (this.props.handleExportImage) {
          // *: Call export image after drawing ECG Viewer completely
          this.props.handleExportImage();
        }
        if (isInitalARBeatMode) {
          this.enterARBeatMode();
        }
      });
    }
  }

  // *: Use componentDidUpdate to update chart data
  /**
   * Refreshes the already built ECG chart when incoming ECG data changes, comparing sample lengths, viewer object, beat markers, hidden channels and both filter values against the previous props. On a detected change it rebuilds the config and annotations and calls ecgChart.updateData, re-attaches the event listeners, resets the completed, fine-grid, view-all and selection flags, then merges initialStateValue with the new chart, channel and filter values into state.
   *
   * @param prevProps - The props from before the update, used to diff the ECG payload.
   * @returns Nothing.
   */
  componentDidUpdate(prevProps) {
    const { ecg } = this.props;
    if (!_.isEmpty(ecg) && ecg.dataSamples && !_.isEmpty(prevProps.ecg)) {
      let needUpdateChartData = false;
      if (
        !prevProps.ecg.dataSamples ||
        ecg.dataSamples[0].length !== prevProps.ecg.dataSamples[0].length ||
        !_.isEqual(ecg.viewerObject, prevProps.ecg.viewerObject) ||
        !_.isEqual(ecg.beatMarkers, prevProps.ecg.beatMarkers) ||
        !_.isEqual(ecg.hiddenChannels, prevProps.ecg.hiddenChannels) ||
        !_.isEqual(ecg.hpFilters, prevProps.ecg.hpFilters) ||
        !_.isEqual(ecg.notchFilters, prevProps.ecg.notchFilters) ||
        ecg.dataSamples[0] !== prevProps.ecg.dataSamples[0]
      ) {
        needUpdateChartData = true;
      }
      if (needUpdateChartData) {
        const { ecgChart } = this.state;
        const {
          configChart,
          missingSampleLength,
          isShowViewFullEventChart,
          timeObject,
          startStudyDate,
          facilityCountry,
        } = this.props;
        const { isDisplayScrollbar } = configChart;
        const {
          dataSamples,
          beatMarkers,
          viewerObject,
          hpFilters,
          notchFilters,
          hiddenChannels,
          eventPos,
          diagnosticLead,
          isShowBeatMarkers,
          speed,
          amp,
          wndDuration,
          samplingRate,
          hesBeatStatus,
          isShowVES,
          isShowSVES,
          missingSampleLengthPre,
          missingSampleLengthPost,
        } = ecg;
        const config = {
          speed: speed ?? 25,
          gain: amp ?? 10,
          wndDuration: wndDuration ?? 10,
          LFF: hpFilters ?? 0,
          HFF: notchFilters ?? 0,
          prevChanIdx:
            hiddenChannels &&
            hiddenChannels.length &&
            _.includes(hiddenChannels, diagnosticLead - 1)
              ? diagnosticLead - 1
              : 0,
          isShowBeatMarkers,
          samplingRate,
        };
        const annotations =
          !isShowViewFullEventChart && viewerObject ? viewerObject.widgets : JSON.parse('null');
        ecgChart.updateData({
          rawDataFromNetwork: dataSamples,
          config,
          beatMarkerArr: beatMarkers,
          hesBeatStatus,
          annotations,
          isDisplayScrollbar,
          hiddenChannels,
          eventPos,
          missingSampleLength,
          isShowVES,
          isShowSVES,
          diagnosticLead,
          timeObject,
          startStudyDate,
          facilityCountry,
          missingSampleLengthPre,
          missingSampleLengthPost,
        });
        this.handleAddEventListener();
        this.haveCompleted = false;
        this.isClickFineGridCurrent = false;
        this.isInViewAllModeCurrent = false;
        this.isSelectingHLine = false;
        this.isSelectingVLine = false;
        this.isSelectingPattern = false;
        const setStateObject = {
          ...initialStateValue,
          ecgChart,
          prevChanIdx:
            hiddenChannels &&
            hiddenChannels.length &&
            _.includes(hiddenChannels, diagnosticLead - 1)
              ? diagnosticLead - 1
              : 0,
          dataLength: dataSamples ? dataSamples.length : 0,
          isLoadedData: true,
          spdValue: speed ?? 25,
          ampValue: amp ?? 10,
        };
        if (hiddenChannels && hiddenChannels.length) {
          _.assign(setStateObject, {
            visibleChannels: hiddenChannels,
            beforeZoomVisibleChannels: hiddenChannels,
          });
        }
        if (!_.isNil(hpFilters)) {
          _.assign(setStateObject, { hiPValue: hpFilters });
        }
        if (!_.isNil(notchFilters)) {
          _.assign(setStateObject, { loPValue: notchFilters });
        }
        if (!_.isNil(isShowBeatMarkers)) {
          _.assign(setStateObject, { isShowBeatMarkers });
        }
        // oxlint-disable-next-line react/no-did-update-set-state
        this.setState(setStateObject);
      }
    }
  }

  /**
   * Tears the viewer down when it leaves the screen. Removes the registered canvas event listeners and clears the pending snackbar timeout.
   *
   * @returns Nothing.
   */
  componentWillUnmount() {
    this.handleRemoveEventListener();
    clearTimeout(this.timeoutSnackbar);
  }

  handleKeyDown = (event) => {
    if (event?.repeat) {
      return;
    }
    if (this.props.isHideAddRemoveBeatMenu) {
      return;
    }
    const { key } = event;
    const { isInViewAllMode, isInWaitingZoomMode, isInZoomMode, isVisibleCapturePopover } =
      this.state;
    switch (key?.toLowerCase()) {
      case 'b': {
        if (!isInViewAllMode && !isInWaitingZoomMode && !isInZoomMode && !isVisibleCapturePopover) {
          this.toggleARBeatMode();
        }
        break;
      }
      default:
    }
  };

  updateSelectedBeatMarker = (selectedBeatMarkerObject, clickedOffsetX) => {
    if (selectedBeatMarkerObject) {
      const {
        selectedBeatMarker,
        selectedBeatMarkerIndex,
        selectedBeatMarkerSample,
        selectedBeatMarkerEvent,
      } = selectedBeatMarkerObject;
      this.selectedBeatMarker = selectedBeatMarker;
      this.selectedBeatMarkerIndex = selectedBeatMarkerIndex;
      this.selectedBeatMarkerSample = selectedBeatMarkerSample;
      this.selectedBeatMarkerEvent = selectedBeatMarkerEvent;
      const { text } = selectedBeatMarker;
      this.setState({ beatType: text.text, removePermission: true });
    } else {
      this.selectedBeatMarker = null;
      this.selectedBeatMarkerIndex = null;
      this.selectedBeatMarkerSample = null;
      this.selectedBeatMarkerEvent = null;
      this.clickedOffsetX = clickedOffsetX;
      this.setState({ removePermission: false });
    }
  };

  exportImage = () => {
    const { callbackImageUrl } = this.props;
    const { allIndexStripSvg, allStripSvg } = this.state.ecgChart.exportAll();
    callbackImageUrl(allIndexStripSvg, allStripSvg);
  };

  handleSubmit = () => {
    const {
      ecgChart,
      hiPValue,
      loPValue,
      beforeZoomVisibleChannels,
      spdValue,
      ampValue,
      isShowBeatMarkers,
    } = this.state;
    const { ecg, onSubmit } = this.props;
    const newEcg = _.cloneDeep(ecg);
    newEcg.viewerObject.version = 'v2';
    newEcg.viewerObject.widgets = ecgChart.getWidgets();
    newEcg.beatMarkers = ecgChart.getBeatMarkers();
    newEcg.hesBeatStatus = ecgChart.getHesBeatStatus();
    newEcg.speed = spdValue;
    newEcg.amp = ampValue;
    newEcg.wndDuration = ecgChart.getWndDuration();
    newEcg.hpFilters = hiPValue;
    newEcg.notchFilters = loPValue;
    newEcg.hiddenChannels = beforeZoomVisibleChannels;
    newEcg.isShowBeatMarkers = isShowBeatMarkers;
    onSubmit({ ecg: newEcg, eventStatus: EVENT_STATUSES.EDITED });
  };

  selectObject = (event) => {
    // *: Disable default browser contextmenu and custom context menu
    const { isInMode } = this.state;

    if (isInMode) {
      event.stopPropagation();
      event.preventDefault();
      return;
    }

    if (!this.isInZoomMode) {
      const { ecgChart } = this.state;
      if (!isInMode) {
        ecgChart.saveContextMenuPosition({ x: event.offsetX, y: event.offsetY });
      }
      this.setState({
        shouldDelete: ecgChart.getSelectingStatus(),
        colorLine: ecgChart.getLineColor(),
      });
    }
  };

  handleAddEventListener = () => {
    this.canvasObject.addEventListener('contextmenu', this.eventSelectObject, false);
    this.canvasObject.addEventListener('mouseover', this.eventDrawMouseOver, false);
    this.canvasObject.addEventListener('mouseout', this.eventDrawMouseOut, false);
    window.addEventListener('keydown', this.handleKeyDown);
  };

  handleRemoveEventListener = () => {
    this.canvasObject.removeEventListener('contextmenu', this.eventSelectObject, false);
    this.canvasObject.removeEventListener('mouseover', this.eventDrawMouseOver, false);
    this.canvasObject.removeEventListener('mouseout', this.eventDrawMouseOut, false);
    window.removeEventListener('keydown', this.handleKeyDown);
  };

  deleteObject = () => {
    this.state.ecgChart.deleteObject();
  };

  toggleViewAllMode = () => {
    this.isInViewAllModeCurrent = !this.isInViewAllModeCurrent;
    this.setState({
      isInViewAllMode: this.isInViewAllModeCurrent,
      isClickFineGrid: this.isInViewAllModeCurrent ? false : this.isClickFineGridCurrent,
    });
  };

  updateSpd = (speed) => {
    this.setState({ spdValue: speed });
  };

  updateAmp = (gain) => {
    this.setState({ ampValue: gain });
  };

  updatePrevChanIdx = (prevChanIdx) => {
    this.setState({ prevChanIdx });
  };

  updateCompletedStatus = (isInMode, haveCompleted) => {
    this.haveCompleted = haveCompleted;
    this.setState({ isInMode });
  };

  updateSelectingHLineStatus = (flag) => {
    this.isSelectingHLine = flag;
  };

  updateSelectingVLineStatus = (flag) => {
    this.isSelectingVLine = flag;
  };

  updateSelectingPatternStatus = (flag) => {
    this.isSelectingPattern = flag;
  };

  updateARBeatModeStatus = (flag) => {
    this.setState({ isInARBeatMode: flag });
  };

  updateWaitingZoomModeStatus = (flag) => {
    this.setState({ isInWaitingZoomMode: flag });
  };

  updateZoomModeStatus = (flag) => {
    this.setState({ isInZoomMode: flag });
  };

  updateVisibleChannels = (visibleChannels, isDrawingBCC = false) => {
    const { isInZoomMode } = this.state;
    this.setState((prevState) => ({
      visibleChannels,
      beforeZoomVisibleChannels:
        isInZoomMode || isDrawingBCC ? prevState.beforeZoomVisibleChannels : visibleChannels,
    }));
  };

  updateSelectingBeatStatus = (isSelectingBeat) => {
    this.setState({ isSelectingBeat });
  };

  onClickHorizCaliperMenu = () => {
    this.state.ecgChart.drawAnnotation(ANNOTATION_TYPE.HLINE);
    this.setState({ isInMode: true });
  };

  onClickVertCaliperMenu = () => {
    this.state.ecgChart.drawAnnotation(ANNOTATION_TYPE.VLINE);
    this.setState({ isInMode: true });
  };

  handleDrawMouseOver = () => {
    // To stop scroll when move mouse from outside to inside chart
    this.state.ecgChart.handleDrawMouseOver();
  };

  handleDrawMouseOut = (event) => {
    const { layerY, offsetX } = event;
    this.state.ecgChart.handleDrawMouseOut({
      scrollDirection: this.scrollDirection,
      offsetX,
      layerY,
    });
  };

  handleChangeColor = (color, isMouseOver) => {
    const { ecgChart } = this.state;
    if (this.isSelectingHLine) {
      ecgChart.changeHLineColor(color);
    } else if (this.isSelectingVLine) {
      ecgChart.changeVLineColor(color);
    } else {
      ecgChart.changePatternColor(color);
    }
    if (!isMouseOver) {
      this.setState({ colorLine: color });
    }
  };

  onClickBeatCalipersMenu = () => {
    this.state.ecgChart.drawAnnotation(ANNOTATION_TYPE.BCC);
  };

  onClickTextMenu = () => {
    this.state.ecgChart.drawAnnotation(ANNOTATION_TYPE.TEXT);
    this.setState({ isInMode: true });
  };

  onClickMarkASampleMenu = (seconds) => {
    this.state.ecgChart.markASample(seconds);
  };

  onClickMarkAPatternMenu = () => {
    this.state.ecgChart.drawAnnotation(ANNOTATION_TYPE.RECT_SELECT);
    this.setState({ isInMode: true });
  };

  onClickShowHideBeatMarkersMenu = () => {
    const { ecgChart, isShowBeatMarkers } = this.state;
    const newIsShowBeatMarkers = !isShowBeatMarkers;
    ecgChart.showHideBeatMarkers(newIsShowBeatMarkers, true);
    this.setState({ isShowBeatMarkers: newIsShowBeatMarkers });
  };

  handleChangeSpdDropdown = (speed) => {
    this.state.ecgChart.updateSpeed(speed);
    this.updateSpd(speed, true);
  };

  handleChangeAmpDropdown = (gain) => {
    this.state.ecgChart.updateGain(gain);
    this.updateAmp(gain, true);
  };

  handleChangeHiLoPDropdown = (isHighPass, freq) => {
    this.state.ecgChart.updateHighLowPass(isHighPass, freq);
    this.setState({ [isHighPass ? 'hiPValue' : 'loPValue']: freq });
  };

  handleChangePrevChanDropdown = (prevChanIdx) => {
    this.state.ecgChart.updatePrevChanIdx(prevChanIdx);
    this.updatePrevChanIdx(prevChanIdx);
  };

  handleChangeSHDropdown = (chIdx) => {
    const { ecgChart, isInZoomMode } = this.state;
    const visibleChannels = ecgChart.getVisibleChannels();
    const setStateObject = {};
    if (_.includes(visibleChannels, chIdx)) {
      if (visibleChannels.length > 1) {
        _.remove(visibleChannels, (x) => x === chIdx);
        _.assign(setStateObject, {
          visibleChannels,
        });
        if (!isInZoomMode) {
          _.assign(setStateObject, {
            beforeZoomVisibleChannels: visibleChannels,
          });
        }
        ecgChart.setVisibleChannels(visibleChannels);
      }
    } else {
      visibleChannels.push(chIdx);
      _.assign(setStateObject, {
        visibleChannels,
      });
      if (!isInZoomMode) {
        _.assign(setStateObject, {
          beforeZoomVisibleChannels: visibleChannels,
        });
      }
      ecgChart.setVisibleChannels(visibleChannels);
    }
    this.setState(setStateObject);
  };

  onChangeInput = (name, value) => {
    this.setState({ [name]: value });
  };

  onClickCaptureMenu = async () => {
    // this.setState({ isCapturingImages: true });
    // const { ecgChart, captureDuration, highlightChannel } = this.state;
    // const { draw, allIndexStripSvg } = ecgChart.handleCapture(captureDuration, highlightChannel);
    // // await this.props.handleCaptureImages(draw, allIndexStripSvg);
    // this.setState({ isVisibleCapturePopover: false, isCapturingImages: false });
  };

  onClickFineGridMenu = () => {
    this.state.ecgChart.toggleFineGrid();
    this.isClickFineGridCurrent = !this.isClickFineGridCurrent;
    this.setState({ isClickFineGrid: this.isClickFineGridCurrent });
  };

  enterARBeatMode = (beatTypeNumber) => {
    this.state.ecgChart.enterARBeatMode(beatTypeNumber);
  };

  exitARBeatMode = () => {
    const { ecgChart, isInARBeatMode } = this.state;
    if (isInARBeatMode) {
      ecgChart.exitARBeatMode();
    }
  };

  toggleARBeatMode = () => {
    const { ecgChart, isInARBeatMode } = this.state;
    if (!isInARBeatMode) {
      ecgChart.enterARBeatMode();
    } else {
      ecgChart.exitARBeatMode();
    }
  };

  onClickZoomMenu = () => {
    const { ecgChart, isInWaitingZoomMode, isInZoomMode } = this.state;
    if (!isInWaitingZoomMode && !isInZoomMode) {
      ecgChart.startZoom();
      this.updateWaitingZoomModeStatus(true);
    } else {
      ecgChart.exitZoom();
      this.updateWaitingZoomModeStatus(false);
    }
  };

  onClickViewAllMenu = () => {
    const { ecgChart, isInViewAllMode } = this.state;
    if (isInViewAllMode) {
      ecgChart.exitViewAllMode();
    } else {
      ecgChart.enterViewAllMode();
    }
    this.toggleViewAllMode();
  };

  handleOpenSnackbar = (message = '') => {
    clearTimeout(this.timeoutSnackbar);
    this.setState({ snackbarMessage: message });
    this.timeoutSnackbar = setTimeout(() => {
      this.setState({ snackbarMessage: '' });
    }, 2000);
  };

  updateBeatTypeNumber = (value) => {
    this.setState({ beatTypeNumber: value });
  };

  onVisibleChangeCapturePopover = () => {
    const { ecgChart, visibleChannels, isVisibleCapturePopover, captureDuration } = this.state;
    const remainSeconds = ecgChart.getRemainSeconds();
    const disabledCaptureDurationIndexes = [];
    if (remainSeconds < 30) {
      disabledCaptureDurationIndexes.push(2);
    }
    if (remainSeconds < 20) {
      disabledCaptureDurationIndexes.push(1);
    }
    if (remainSeconds < 10) {
      disabledCaptureDurationIndexes.push(0);
    }
    const disabledHighlightChannelIndexes = _.xor([0, 1, 2], visibleChannels);
    const highlightChannel = _.includes(
      disabledHighlightChannelIndexes,
      this.state.highlightChannel,
    )
      ? visibleChannels[0]
      : this.state.highlightChannel;
    this.setState({
      isVisibleCapturePopover: !isVisibleCapturePopover,
      disabledCaptureDurationIndexes,
      disabledHighlightChannelIndexes,
      highlightChannel,
      captureDuration: disabledCaptureDurationIndexes.length ? 10 : captureDuration,
    });
  };

  renderContextMenu = () => {
    const {
      shouldDelete,
      colorLine,
      isLoadedData,
      isInViewAllMode,
      isInMode,
      isInWaitingZoomMode,
      isInARBeatMode,
      spdValue,
      isShowBeatMarkers,
      removePermission,
      beatType,
      ecgChart,
    } = this.state;
    const { isHideMarkASampleMenu, hasUpdateBeatContextMenu, idComponents } = this.props;

    if (isLoadedData) {
      if (
        !isInMode &&
        !isInViewAllMode &&
        !isInWaitingZoomMode &&
        !isInARBeatMode &&
        !this.haveCompleted
      ) {
        if (shouldDelete) {
          if (this.isSelectingHLine || this.isSelectingPattern || this.isSelectingVLine) {
            return (
              <ContextMenu id={idComponents.contextMenu}>
                <SubMenu
                  className="submenu-hline"
                  title={t`Change Color`}
                  hoverDelay={100}
                  onMouseMove={() => {
                    this.handleChangeColor(colorLine, true);
                  }}
                >
                  <TwitterPicker
                    width="168px"
                    onChange={(color) => {
                      this.handleChangeColor(color.hex, false);
                    }}
                    colors={[
                      '#0000FF',
                      '#ffd600',
                      '#A3CB38',
                      '#009432',
                      '#00e5c3',
                      '#00A8FF',
                      '#E056FD',
                      '#ffA801',
                    ]}
                    triangle="hide"
                  />
                </SubMenu>
                <MenuItem onClick={this.deleteObject}>Delete</MenuItem>
              </ContextMenu>
            );
          }

          return (
            <ContextMenu id={idComponents.contextMenu}>
              <MenuItem onClick={this.deleteObject}>Delete</MenuItem>
            </ContextMenu>
          );
        }

        return (
          <ContextMenu id={idComponents.contextMenu}>
            {hasUpdateBeatContextMenu &&
              (removePermission ? (
                <>
                  <MenuItem
                    onClick={() => {
                      ecgChart.deleteBeatMarker(
                        this.selectedBeatMarker,
                        this.selectedBeatMarkerIndex,
                      );
                    }}
                  >
                    Remove Beat
                  </MenuItem>
                  <SubMenu
                    title={t`Change Beat Type`}
                    hoverDelay={100}
                    className="peak-ecg-sub-menu"
                  >
                    <MenuItem
                      defaultSelected={beatType === 'N'}
                      onClick={() => {
                        ecgChart.changeBeatMarkerType(
                          this.selectedBeatMarker,
                          this.selectedBeatMarkerIndex,
                          1,
                        );
                      }}
                    >
                      N
                    </MenuItem>
                    <MenuItem
                      defaultSelected={beatType === 'V'}
                      onClick={() => {
                        ecgChart.changeBeatMarkerType(
                          this.selectedBeatMarker,
                          this.selectedBeatMarkerIndex,
                          60,
                        );
                      }}
                    >
                      V
                    </MenuItem>
                    <MenuItem
                      defaultSelected={beatType === 'S'}
                      onClick={() => {
                        ecgChart.changeBeatMarkerType(
                          this.selectedBeatMarker,
                          this.selectedBeatMarkerIndex,
                          70,
                        );
                      }}
                    >
                      S
                    </MenuItem>
                    <MenuItem
                      defaultSelected={beatType === 'Q'}
                      onClick={() => {
                        ecgChart.changeBeatMarkerType(
                          this.selectedBeatMarker,
                          this.selectedBeatMarkerIndex,
                          80,
                        );
                      }}
                    >
                      Q
                    </MenuItem>
                  </SubMenu>
                </>
              ) : (
                isShowBeatMarkers && (
                  <SubMenu title={t`Add Beats`} hoverDelay={100} className="peak-ecg-sub-menu">
                    <MenuItem
                      onClick={() => {
                        this.enterARBeatMode(1);
                      }}
                    >
                      N
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        this.enterARBeatMode(60);
                      }}
                    >
                      V
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        this.enterARBeatMode(70);
                      }}
                    >
                      S
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        this.enterARBeatMode(80);
                      }}
                    >
                      Q
                    </MenuItem>
                  </SubMenu>
                )
              ))}
            <MenuItem onClick={this.onClickHorizCaliperMenu}>Horizontal Caliper</MenuItem>
            <MenuItem onClick={this.onClickVertCaliperMenu}>Vertical Caliper</MenuItem>
            {!isInViewAllMode ? (
              <MenuItem onClick={this.onClickBeatCalipersMenu}>Beat Calipers</MenuItem>
            ) : null}
            <MenuItem onClick={this.onClickTextMenu}>Text Annotation</MenuItem>
            {!isHideMarkASampleMenu && (
              <>
                <MenuItem
                  onClick={() => {
                    this.onClickMarkASampleMenu(10);
                  }}
                >
                  Mark 10 Sec
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    this.onClickMarkASampleMenu(20);
                  }}
                >
                  Mark 20 Sec
                </MenuItem>
              </>
            )}
            <MenuItem onClick={this.onClickMarkAPatternMenu}>Mark a Pattern</MenuItem>
            {!isInViewAllMode && spdValue !== 2.5 && (
              <MenuItem onClick={this.onClickShowHideBeatMarkersMenu}>
                {`${isShowBeatMarkers ? 'Hide' : 'Show'} Beat Markers`}
              </MenuItem>
            )}
          </ContextMenu>
        );
      }
    }

    // *: Prevent show menu after right click to draw annotation
    if (this.haveCompleted) {
      this.haveCompleted = false;
    }

    return null;
  };

  /**
   * Renders the ECG canvas stack and, in edit mode, the full review toolbar. Derives every per-tool disable flag from the zoom, waiting-zoom, view-all, mode and beat-marker states, then draws the optional scroller with its preview-channel context menu and previous/next minute arrows, the background, chart and object canvases, and the speed, gain, filter, channel, capture, beat-marker, zoom, fine-grid and view-all controls; outside edit mode only the canvases are returned.
   *
   * @returns The rendered viewer element.
   */
  render() {
    const {
      configChart,
      isDisplayCaptureMenu,
      isHideFineGridMenu,
      isHideAddRemoveBeatMenu,
      idComponents,
      hasArrowButtonScroller,
      handleClickArrowButton,
    } = this.props;
    const {
      isLoadedData,
      spdValue,
      ampValue,
      hiPValue,
      loPValue,
      visibleChannels,
      prevChanIdx,
      isInARBeatMode,
      isInWaitingZoomMode,
      isInZoomMode,
      isInViewAllMode,
      isInMode,
      isShowBeatMarkers,
      beatTypeNumber,
      isSelectingBeat,
      snackbarMessage,
      captureDuration,
      highlightChannel,
      isCapturingImages,
      disabledHighlightChannelIndexes,
      isVisibleCapturePopover,
      disabledCaptureDurationIndexes,
      isClickFineGrid,
    } = this.state;
    const { isEdit, isDisplayScrollbar } = configChart;
    const isDisableTooltipSpeed = isInViewAllMode || isInMode || isInWaitingZoomMode;
    const isDisabledTooltipAmp = isInMode || isInWaitingZoomMode;
    const isDisabledTooltipHiPass = isInMode || isInWaitingZoomMode;
    const isDisabledTooltipLoPass = isInMode || isInWaitingZoomMode;
    const isDisabledTooltipSHChannel = isInMode || isInWaitingZoomMode;
    const isDisabledTooltipPreviewChannel = isInMode || isInWaitingZoomMode;
    const isDisabledTooltipCapture = isInViewAllMode || isInMode || isInWaitingZoomMode;
    const isDisabledTooltipFineGrid = isInViewAllMode || isInMode || isInWaitingZoomMode;
    const isDisableTooltipAddRemoveBeat =
      isInWaitingZoomMode || isInViewAllMode || isInMode || !isShowBeatMarkers || isInZoomMode;
    const isDisableTooltipZoom = isInViewAllMode || isInMode;
    const isDisableTooltipViewAll = isInWaitingZoomMode || isInZoomMode || isInMode;

    if (isEdit) {
      return (
        <div id="ecg-viewer" className="ecg-viewer">
          {isDisplayScrollbar && (
            <div className="chart-scroller">
              <Dropdown
                disabled={isDisabledTooltipPreviewChannel}
                trigger={['contextMenu']}
                overlay={
                  <div id="ecgPreviewChannel">
                    <div className="title">{`${t`Preview channel`}:`}</div>
                    <Radio.Group value={prevChanIdx}>
                      {this.arrayChannelPrevChan.map((prevChan, index) => {
                        if (index < this.state.dataLength) {
                          return (
                            <Radio
                              id={`preview-channel-${index}`}
                              name="previewChannel"
                              aria-label={prevChan}
                              onClick={() => {
                                this.handleChangePrevChanDropdown(index);
                              }}
                              value={index}
                            >
                              {prevChan}
                            </Radio>
                          );
                        }
                        return null;
                      })}
                    </Radio.Group>
                  </div>
                }
              >
                <div>
                  <canvas id={idComponents.canvasScroller} />
                </div>
              </Dropdown>
              {hasArrowButtonScroller && (
                <>
                  <CustomTooltip title={t`Previous minute`} placement="bottom">
                    <div>
                      <IconButton
                        id="left-arrow-btn"
                        aria-label={t`Previous minute`}
                        className="left-arrow-btn"
                        iconComponent={<img src={navigationLeftIcon} alt="Navigation left icon" />}
                        onClick={() => {
                          handleClickArrowButton();
                        }}
                      />
                    </div>
                  </CustomTooltip>
                  <CustomTooltip title={t`Next minute`} placement="bottom">
                    <div>
                      <IconButton
                        id="right-arrow-btn"
                        aria-label={t`Next minute`}
                        className="right-arrow-btn"
                        iconComponent={
                          <img src={navigationRightIcon} alt="Navigation right icon" />
                        }
                        onClick={() => {
                          handleClickArrowButton(true);
                        }}
                      />
                    </div>
                  </CustomTooltip>
                </>
              )}
            </div>
          )}

          <div id="canvas-chart-container" className="chart-graph">
            <SnackbarMaterial
              open={!!snackbarMessage}
              message={<span className="d-flex align-items-center">{snackbarMessage}</span>}
              className="snackbar--center-inside"
            />

            <ContextMenuTrigger id={idComponents.contextMenu} holdToDisplay={-1}>
              <div className="canvas-background">
                <canvas className="canvas-background-1" id={idComponents.canvasBackground} />
                <canvas className="canvas-background-2" id={idComponents.exportGridCanvas} />
              </div>
              <div className="canvas-chart">
                <canvas id={idComponents.canvasChart} />
              </div>
              <div className={`canvas-object-${idComponents.canvasObject}`}>
                <canvas id={idComponents.canvasObject} />
              </div>
            </ContextMenuTrigger>
          </div>

          {isLoadedData && (
            <Row id="ecgToolbarRow">
              <div className="toolbar-button">
                <div className="toolbar-zone">
                  <ECGToolButton
                    disabled={isDisableTooltipSpeed}
                    type={TOOLBAR_TYPE.SPEED}
                    value={spdValue}
                    onChange={this.handleChangeSpdDropdown}
                  />
                  <ECGToolButton
                    disabled={isDisabledTooltipAmp}
                    type={TOOLBAR_TYPE.GAIN}
                    value={ampValue}
                    onChange={this.handleChangeAmpDropdown}
                  />
                </div>
                <div className="toolbar-zone">
                  <ECGToolButton
                    disabled={isDisabledTooltipHiPass}
                    type={TOOLBAR_TYPE.HIGH_PASS}
                    value={hiPValue}
                    onChange={(value) => {
                      this.handleChangeHiLoPDropdown(true, value);
                    }}
                  />
                  <ECGToolButton
                    disabled={isDisabledTooltipLoPass}
                    type={TOOLBAR_TYPE.LOW_PASS}
                    value={loPValue}
                    onChange={(value) => {
                      this.handleChangeHiLoPDropdown(false, value);
                    }}
                  />
                </div>
                <div className="toolbar-zone">
                  <ToggleShChannel
                    disabled={isDisabledTooltipSHChannel}
                    visibleChannels={visibleChannels}
                    onChange={this.handleChangeSHDropdown}
                  />
                </div>
              </div>
              {isInARBeatMode ? (
                <div className="toolbar-button">
                  <BeatMarkerButtons
                    handleChangeBeat={this.enterARBeatMode}
                    beatTypeNumber={beatTypeNumber}
                    onCloseARBeatMode={this.toggleARBeatMode}
                    isSelectingBeat={isSelectingBeat}
                  />
                </div>
              ) : (
                <div className="toolbar-button">
                  {isDisplayCaptureMenu && (
                    <Popover
                      zIndex={1}
                      classNames={{ root: 'capture-strip-popover' }}
                      content={
                        <div className="capture-menu-container">
                          <ToggleInput
                            name="captureDuration"
                            label={t`Capture duration`}
                            options={this.captureDurationOptions}
                            value={captureDuration}
                            onChange={this.onChangeInput}
                            disabledIndexes={disabledCaptureDurationIndexes}
                            disabled={isCapturingImages}
                          />
                          <ToggleInput
                            name="highlightChannel"
                            label={t`Highlight channel`}
                            className="mt-3 mb-3"
                            options={this.highlightChannelOptions}
                            value={highlightChannel}
                            onChange={this.onChangeInput}
                            disabledIndexes={disabledHighlightChannelIndexes}
                            disabled={isCapturingImages}
                          />
                          <NormalButton
                            isFilled
                            buttonName={t`Capture`}
                            className="capture-btn"
                            onClick={this.onClickCaptureMenu}
                            isSaving={isCapturingImages}
                          />
                        </div>
                      }
                      placement="top"
                      trigger="click"
                      open={isVisibleCapturePopover}
                      onOpenChange={this.onVisibleChangeCapturePopover}
                    >
                      <CustomTooltip title={t`Capture`} placement="bottom">
                        <div>
                          <IconButton
                            id="captureButton"
                            aria-label={t`Capture`}
                            isFilled
                            iconComponent={<img src={captureIconGreen} alt="Capture icon" />}
                            disabled={isDisabledTooltipCapture}
                            onClick={this.onVisibleChangeCapturePopover}
                          />
                        </div>
                      </CustomTooltip>
                    </Popover>
                  )}
                  {!isHideAddRemoveBeatMenu && (
                    <CustomButton
                      type="outline"
                      size="small"
                      id="addRemoveBeatMarkerButton"
                      tooltipTitle={t`Beat marker tool`}
                      tooltipPlacement="bottom"
                      disabled={isDisableTooltipAddRemoveBeat}
                      icon={<ReactSVG src={AddRemoveBeatMarkerIconZoomOutIcon} />}
                      onClick={this.toggleARBeatMode}
                      ariaLabel={t`Beat marker tool`}
                    />
                  )}
                  <CustomButton
                    id="zoomButton"
                    type="outline"
                    onClick={this.onClickZoomMenu}
                    tooltipTitle={isInZoomMode ? t`Zoom out` : t`Zoom in`}
                    size="small"
                    tooltipPlacement="bottom"
                    icon={<ReactSVG src={isInZoomMode ? ZoomOutIcon : ZoomInIcon} />}
                    disabled={
                      isDisableTooltipZoom || (!isInZoomMode && spdValue === 500 && ampValue >= 100)
                    }
                    ariaLabel={isInZoomMode ? t`Zoom out` : t`Zoom in`}
                  />
                  {!isHideFineGridMenu && (
                    <CustomButton
                      id="fineGridButton"
                      type="outline"
                      onClick={this.onClickFineGridMenu}
                      tooltipTitle={isClickFineGrid ? t`Normal grid` : t`Fine grid`}
                      size="small"
                      tooltipPlacement="bottom"
                      icon={<ReactSVG src={isClickFineGrid ? FineGridIcon : NormalGridIcon} />}
                      disabled={isDisabledTooltipFineGrid}
                      ariaLabel={isClickFineGrid ? t`Normal grid` : t`Fine grid`}
                    />
                  )}
                  <CustomButton
                    id="viewAllButton"
                    type="outline"
                    onClick={this.onClickViewAllMenu}
                    tooltipTitle={isInViewAllMode ? t`Exit view all mode` : t`View all`}
                    size="small"
                    tooltipPlacement="bottom"
                    icon={<ReactSVG src={isInViewAllMode ? ExitViewAllIcon : ViewAllIcon} />}
                    disabled={isDisableTooltipViewAll}
                    ariaLabel={isInViewAllMode ? t`Exit view all mode` : t`View all`}
                  />
                </div>
              )}
            </Row>
          )}
          {this.renderContextMenu()}
        </div>
      );
    }

    return (
      <div id="ecg-viewer" className="ecg-viewer">
        {isDisplayScrollbar && (
          <div className="chart-scroller">
            <canvas id={idComponents.canvasScroller} />
          </div>
        )}
        <div id="canvas-chart-container" className="chart-graph">
          <div className="canvas-background">
            <canvas id={idComponents.canvasBackground} />
            <canvas id={idComponents.exportGridCanvas} />
          </div>
          <div className="canvas-chart">
            <canvas id={idComponents.canvasChart} />
          </div>
          <div className={`canvas-object-${idComponents.canvasObject}`}>
            <canvas id={idComponents.canvasObject} />
          </div>
        </div>
      </div>
    );
  }
}

ECGViewer.defaultProps = {
  updateMinMaxHr: () => {},
  timeObject: {},
  idComponents: {
    canvasBackground: 'my-canvas1-1',
    exportGridCanvas: 'my-canvas1-2',
    canvasChart: 'my-canvas2',
    canvasObject: 'my-canvas3',
    canvasScroller: 'my-canvas4',
    contextMenu: 'my-canvas-context-menu',
  },
  isHideFineGridMenu: true,
  isDisplayCaptureMenu: false,
  isHideAddRemoveBeatMenu: false,
  isHideMarkASampleMenu: false,
  hasUpdateBeatContextMenu: true,
  hasArrowButtonScroller: false,
  handleClickArrowButton: () => {},
  isInitalARBeatMode: false,
  handleUpdateBeat: () => {},
};

export default ECGViewer;
